<script setup lang="ts">
  import { computed } from 'vue';
  import PaperclipIcon from '@/shared/assets/icons/PaperclipIcon.svg';
  import { IconImage } from '@/shared/ui';
  import type { TrainingDetail } from '@/entities/Training';
  import { formatCivilDateToPtBR } from '@/shared/lib';
  import { formatTrainingControlValue } from '../../lib/formatTrainingControlValue';
  import { getFileNameFromObjectName } from '../../lib/getFileNameFromObjectName';
  import { useInstructorCurriculumViewer } from '../../model/useInstructorCurriculumViewer';

  /**
   * `CONTROL` is the printed quality record, which lays every field out in equal thirds.
   * `VIEW` is the Dados Treinamento tab, where the prototype gives the Interno/Externo
   * indication a full-width row of its own and sizes the remaining fields like the
   * Programação Treinamento section above it.
   */
  const props = withDefaults(
    defineProps<{
      instructors: TrainingDetail['instructors'];
      instructorType: TrainingDetail['instructorType'];
      externalInstructorCnpj: string | null;
      externalInstructorCompanyName: string | null;
      variant?: 'CONTROL' | 'VIEW';
    }>(),
    { variant: 'CONTROL' }
  );

  const isViewVariant = computed(() => props.variant === 'VIEW');

  const fieldsClass = computed(() =>
    isViewVariant.value ? 'flex flex-wrap gap-4' : 'grid gap-4 md:grid-cols-3'
  );

  const isExternalIndividual = computed(() => props.instructorType === 'EXTERNAL_INDIVIDUAL');
  const isExternalCompany = computed(() => props.instructorType === 'EXTERNAL_COMPANY');
  const isExternal = computed(() => isExternalIndividual.value || isExternalCompany.value);
  const isKnownInternal = computed(() => props.instructorType != null && !isExternal.value);
  const instructorKindLabel = computed(() => {
    if (props.instructorType == null) return formatTrainingControlValue(null);
    return isExternal.value ? 'Externo' : 'Interno';
  });

  const { openingSessionIds, openCurriculum } = useInstructorCurriculumViewer();
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      v-for="instructor in instructors"
      :key="instructor.sessionId"
      data-testid="view-training-open-instructor-row"
      class="flex flex-col gap-4 rounded-2xl border border-gray-02 p-4"
    >
      <div v-if="isViewVariant" class="flex flex-col gap-2">
        <span class="text-xs font-semibold text-gray-07">Ministrante:</span>
        <span
          data-testid="view-training-open-instructor-kind"
          class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
        >
          {{ instructorKindLabel }}
        </span>
      </div>

      <div :class="fieldsClass">
        <div v-if="!isViewVariant" class="flex flex-col gap-2">
          <span class="text-xs font-semibold text-gray-07">Ministrante:</span>
          <span
            data-testid="view-training-open-instructor-kind"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ instructorKindLabel }}
          </span>
        </div>

        <div
          v-if="isExternal"
          class="flex flex-col gap-2"
          :class="isViewVariant && 'w-full md:w-67'"
        >
          <span class="text-xs font-semibold text-gray-07">Ministrante Externo:</span>
          <span
            data-testid="view-training-open-instructor-external-kind"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ isExternalCompany ? 'Pessoa Jurídica' : 'Pessoa Física' }}
          </span>
        </div>

        <div
          v-if="isExternalIndividual"
          class="flex flex-col gap-2"
          :class="isViewVariant && 'w-full md:w-67'"
        >
          <span class="text-xs font-semibold text-gray-07">CPF:</span>
          <span
            data-testid="view-training-open-instructor-cpf"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ formatTrainingControlValue(instructor.cpf) }}
          </span>
        </div>
        <div
          v-if="isExternalIndividual"
          class="flex flex-col gap-2"
          :class="isViewVariant && 'w-full md:w-80'"
        >
          <span class="text-xs font-semibold text-gray-07">Profissão:</span>
          <span
            data-testid="view-training-open-instructor-profession"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ formatTrainingControlValue(instructor.profession) }}
          </span>
        </div>

        <div
          v-if="isExternalCompany"
          class="flex flex-col gap-2"
          :class="isViewVariant && 'w-full md:w-67'"
        >
          <span class="text-xs font-semibold text-gray-07">CNPJ:</span>
          <span
            data-testid="view-training-open-instructor-cnpj"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ formatTrainingControlValue(externalInstructorCnpj) }}
          </span>
        </div>
        <div
          v-if="isExternalCompany"
          class="flex flex-col gap-2"
          :class="isViewVariant && 'w-full md:w-80'"
        >
          <span class="text-xs font-semibold text-gray-07">Razão Social:</span>
          <span
            data-testid="view-training-open-instructor-company-name"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ formatTrainingControlValue(externalInstructorCompanyName) }}
          </span>
        </div>

        <div class="flex flex-col gap-2" :class="isViewVariant && 'w-full md:w-67'">
          <span class="text-xs font-semibold text-gray-07">
            {{ isExternal ? 'Ministrante externo:' : 'Ministrante:' }}
          </span>
          <span
            data-testid="view-training-open-instructor-name"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ formatTrainingControlValue(instructor.name) }}
          </span>
        </div>

        <div
          v-if="isKnownInternal"
          class="flex flex-col gap-2"
          :class="isViewVariant && 'w-full md:w-80'"
        >
          <span class="text-xs font-semibold text-gray-07">Cargo:</span>
          <span
            data-testid="view-training-open-instructor-position"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ formatTrainingControlValue(instructor.position?.name) }}
          </span>
        </div>

        <div class="flex flex-col gap-2" :class="isViewVariant && 'w-full md:w-45'">
          <span class="text-xs font-semibold text-gray-07">Data do treinamento:</span>
          <span
            data-testid="view-training-open-instructor-date"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{
              instructor.trainingDate
                ? formatCivilDateToPtBR(instructor.trainingDate)
                : formatTrainingControlValue(null)
            }}
          </span>
        </div>
      </div>

      <div v-if="isExternal && instructor.curriculumObjectName" class="flex flex-col gap-2">
        <span class="text-xs font-semibold text-gray-07">Currículo Ministrante:</span>
        <a
          href="#"
          data-testid="view-training-open-instructor-curriculum"
          :aria-busy="openingSessionIds.includes(instructor.sessionId)"
          class="flex w-fit items-center gap-2 rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-[var(--theme-secondary-ink)] underline"
          @click.prevent="openCurriculum(instructor.sessionId)"
        >
          <IconImage :src="PaperclipIcon" alt="" class="h-5 w-5" />
          {{ getFileNameFromObjectName(instructor.curriculumObjectName) }}
        </a>
      </div>
    </div>

    <p
      v-if="instructors.length === 0"
      data-testid="view-training-open-instructors-empty"
      class="text-sm text-gray-05"
    >
      Nenhum ministrante vinculado ao treinamento
    </p>
  </div>
</template>
